<template>
  <form @submit.prevent="submit">
    <h1 class="container-title" :class="[`text-${titleAlignment}`]">
      {{ title ? "" : formatters.text.capitalize(company.name) }} {{ title ? title : "Sign Up" }}
    </h1>
    <FormMessage :errors="errors" v-if="errors" />
    <FormInput
      name="firstName"
      v-model="fields.firstName"
      testId="first-name-input"
      placeholder="First Name"
      label="First Name *"
      :errors="validation.fields.firstName.errors"
      :dirty="validation.fields.firstName.dirty"
      autocomplete="off"
      :tabindex="1"
    />
    <FormInput
      name="lastName"
      v-model="fields.lastName"
      testId="last-name-input"
      placeholder="Last Name"
      label="Last Name *"
      :errors="validation.fields.lastName.errors"
      :dirty="validation.fields.lastName.dirty"
      autocomplete="off"
      :tabindex="2"
    />
    <FormPassword
      name="password"
      v-model="fields.password"
      type="password"
      testId="password-input"
      placeholder="Password"
      label="Password *"
      :errors="validation.fields.password.errors"
      :dirty="validation.fields.password.dirty"
      :strengthMeter="true"
      v-model:strength="passwordStrength"
      autocomplete="off"
      :tabindex="3"
    />
    <Alert variant="info" :dismissible="false">
      The invitation email address will be used as this user's primary email.
    </Alert>
    <FormCheckbox
      name="acceptTerms"
      v-model="fields.acceptTerms"
      testId="accept-terms-input"
      :label="TOS_MESSAGE"
      :errors="validation.fields.acceptTerms.errors"
      :dirty="validation.fields.acceptTerms.dirty"
    />
    <div class="form-actions">
      <Btn
        variant="primary"
        type="submit"
        :disabled="!submittable"
        :loading="loading"
        data-test="signup-button"
      >
        Join Team
      </Btn>
      <div class="signup-links">
        <div class="sign-up my-2 mt-4">
          Already have an account?
          <BtnLink @click="loginAction()">Login.</BtnLink>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { computed, defineAsyncComponent, reactive, ref } from "vue";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import BtnLink from "@/components/display/BtnLink";
import constants from "@/utils/constants";
import { formatters } from "leatherman-js";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import { fromServer } from "@/utils/translations";
import router from "@/router";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { validations as validationRules } from "leatherman-js";

export default {
  name: "JoinForm",
  components: {
    Alert,
    Btn,
    BtnLink,
    FormCheckbox,
    FormInput,
    FormMessage,
    FormPassword: defineAsyncComponent(() =>
      import(/* webpackChunkName: "form-password" */ "@/components/display/form/FormPassword")
    ),
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    code: {
      type: String,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
    loginAction: {
      type: Function,
      default: () => {
        router.push({ name: constants.ROUTES.LOGIN });
      },
    },
    title: String,
    titleAlignment: {
      type: String,
      default: "center",
    },
  },
  setup(props, { emit }) {
    const TOS_MESSAGE = `I have read and agree to the ${`<a href='${constants.LINKS.NEXCESS.TOS}' target='_blank'>Terms of Service</a>`} and acknowledge receipt of the ${`<a href='${constants.LINKS.NEXCESS.PRIVACY_POLICY}' target='_blank'>Privacy Policy</a>`}.`;

    // display variables
    const loading = ref(false);
    const errors = ref(null);
    const passwordStrength = ref(0);
    const submittable = computed(
      () => !loading.value && validation.errors.length === 0 && passwordStrength.value >= 3
    );

    // signup variables and validation
    const fields = reactive({
      password: null,
      lastName: null,
      firstName: null,
      acceptTerms: false,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules.forms.userCreateOnJoin,
      {},
      {
        firstName: {
          requiredIf: "First name is required",
        },
        lastName: {
          requiredIf: "Last name is required",
        },
        password: {
          required: "Password is required",
        },
        acceptTerms: {
          equal: "You must accept the terms of service to signup.",
        },
      }
    );
    const company = computed(() => store.getters["company/get"]);

    // submit the form
    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          loading.value = true;
          store
            .dispatch("auth/joinWithNewUser", {
              userId: props.userId,
              clientId: props.clientId,
              code: props.code,
              firstName: fields.firstName,
              lastName: fields.lastName,
              password: fields.password,
              passwordConfirmation: fields.password,
            })
            .then(() => {
              emit("success");
            })
            .catch((e) => {
              // if two factor is required, show the two factor code field
              errors.value = fromServer(e.request.response);
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {});
    };

    return {
      errors,
      fields,
      formatters,
      passwordStrength,
      loading,
      submit,
      submittable,
      TOS_MESSAGE,
      validation,
      company,
    };
  },
};
</script>

<style lang="scss" scoped>
.signup-links {
  font-size: 0.75rem;
  text-align: center;
  .forgot {
    margin-bottom: 1rem;
  }
}
</style>
